import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';

// WordPress Direct API configuration
const WORDPRESS_BASE_URL = 'https://www.medias.saint-louis-ruche.com';
const WP_API_BASE = `${WORDPRESS_BASE_URL}/wp-json`;

// HTML entity decoder utility
function decodeHtmlEntities(text) {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

// Clean HTML tags from text
function stripHtmlTags(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

// WordPress API Client that calls WordPress directly (no backend needed)
class WordPressDirectClient {
  constructor() {
    this.baseUrl = WP_API_BASE;
  }

  async get(endpoint, params = {}) {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('WordPress API Error:', error);
      // Return empty data structure on error
      return [];
    }
  }

  // Get videos from WordPress custom post type
  async getVideos(params = {}) {
    const defaultParams = {
      per_page: params.limit || 20,
      page: Math.floor((params.skip || 0) / (params.limit || 20)) + 1,
      _embed: true
    };

    if (params.search) {
      defaultParams.search = params.search;
    }

    if (params.directory) {
      defaultParams.meta_key = 'filename';
      defaultParams.meta_value = params.directory;
      defaultParams.meta_compare = 'LIKE';
    }

    try {
      const videos = await this.get('/wp/v2/slr-videos', defaultParams);
      return {
        videos: videos.map(v => this.transformVideo(v)),
        count: videos.length,
        skip: params.skip || 0,
        limit: params.limit || 20
      };
    } catch (error) {
      console.warn('Custom post type not found, trying fallback...');
      
      // Fallback: try to get from regular posts or media
      try {
        const posts = await this.get('/wp/v2/posts', {
          ...defaultParams,
          categories: await this.getVideosCategoryId()
        });
        return {
          videos: posts.map(p => this.transformPostAsVideo(p)),
          count: posts.length,
          skip: params.skip || 0,
          limit: params.limit || 20
        };
      } catch (fallbackError) {
        console.error('All video endpoints failed:', fallbackError);
        return { videos: [], count: 0, skip: 0, limit: 20 };
      }
    }
  }

  // Get single video
  async getVideo(id) {
    try {
      const video = await this.get(`/wp/v2/slr-videos/${id}`, { _embed: true });
      return this.transformVideo(video);
    } catch (error) {
      // Fallback to regular posts
      try {
        const post = await this.get(`/wp/v2/posts/${id}`, { _embed: true });
        return this.transformPostAsVideo(post);
      } catch (fallbackError) {
        throw new Error('Video not found');
      }
    }
  }

  // Get playlists
  async getPlaylists() {
    try {
      const playlists = await this.get('/wp/v2/slr-playlists', { 
        _embed: true,
        per_page: 100  // Get up to 100 playlists instead of default 10
      });
      return {
        playlists: playlists.map(p => this.transformPlaylist(p))
      };
    } catch (error) {
      console.warn('Playlists endpoint not found, returning empty array');
      return { playlists: [] };
    }
  }

  // Get single playlist
  async getPlaylist(id) {
    try {
      const playlist = await this.get(`/wp/v2/slr-playlists/${id}`, { _embed: true });
      const transformedPlaylist = this.transformPlaylist(playlist);
      
      // Try to get videos for this playlist using multiple strategies
      let videos = [];
      
      // Strategy 1: Use video_ids from meta (if available after plugin update)
      if (transformedPlaylist.video_ids && transformedPlaylist.video_ids.length > 0) {
        console.log('Using video_ids from meta:', transformedPlaylist.video_ids);
        const videoPromises = transformedPlaylist.video_ids.map(videoId => this.getVideo(videoId));
        videos = await Promise.all(videoPromises.map(p => p.catch(() => null)));
        videos = videos.filter(v => v !== null);
      }
      
      // Strategy 2: Parse playlist title and find matching videos by title prefix
      if (videos.length === 0 && transformedPlaylist.name) {
        console.log('Searching videos by playlist name:', transformedPlaylist.name);
        try {
          // Get all videos and filter by title prefix
          const allVideos = await this.getVideos({ limit: 100 });
          videos = allVideos.videos.filter(video => {
            const videoTitle = video.title || '';
            const playlistName = transformedPlaylist.name;
            
            // Check if video title starts with playlist name (like "Fête 2023")
            return videoTitle.toLowerCase().includes(playlistName.toLowerCase()) ||
                   videoTitle.startsWith(`[${playlistName}]`) ||
                   videoTitle.includes(`(${playlistName})`) ||
                   this.extractBracketText(videoTitle) === playlistName;
          });
          console.log(`Found ${videos.length} matching videos by title`);
        } catch (error) {
          console.error('Error searching videos by title:', error);
        }
      }
      
      // Strategy 3: Extract from playlist content description
      if (videos.length === 0 && playlist.content?.rendered) {
        console.log('Trying to extract from playlist content');
        const content = playlist.content.rendered;
        
        // Look for video count in content (like "10 vidéos")
        const videoCountMatch = content.match(/(\d+)\s+vidéos?/i);
        if (videoCountMatch) {
          const expectedCount = parseInt(videoCountMatch[1]);
          console.log(`Playlist should have ${expectedCount} videos`);
          
          // Search for videos with similar titles
          try {
            const allVideos = await this.getVideos({ limit: 200 });
            const playlistWords = transformedPlaylist.name.toLowerCase().split(/\s+/);
            
            videos = allVideos.videos.filter(video => {
              const videoTitle = (video.title || '').toLowerCase();
              return playlistWords.some(word => videoTitle.includes(word));
            }).slice(0, expectedCount);
            
            console.log(`Found ${videos.length} videos by content analysis`);
          } catch (error) {
            console.error('Error in content analysis:', error);
          }
        }
      }
      
      transformedPlaylist.videos = videos;
      transformedPlaylist.video_count = videos.length;
      
      return transformedPlaylist;
    } catch (error) {
      console.error('Error getting playlist:', error);
      throw new Error('Playlist not found');
    }
  }

  // Helper method to extract bracket text from video titles
  extractBracketText(title) {
    if (!title) return null;
    const match = title.match(/^\[([^\]]+)\]/);
    return match ? match[1].trim() : null;
  }

  // Get images
  async getImages(params = {}) {
    const defaultParams = {
      per_page: params.limit || 20,
      page: Math.floor((params.skip || 0) / (params.limit || 20)) + 1,
      _embed: true
    };

    if (params.search) {
      defaultParams.search = params.search;
    }

    try {
      const images = await this.get('/wp/v2/slr-images', defaultParams);
      return {
        images: images.map(i => this.transformImage(i)),
        count: images.length,
        skip: params.skip || 0,
        limit: params.limit || 20
      };
    } catch (error) {
      // Fallback to media library
      try {
        const media = await this.get('/wp/v2/media', {
          ...defaultParams,
          media_type: 'image'
        });
        return {
          images: media.map(m => this.transformMediaAsImage(m)),
          count: media.length,
          skip: params.skip || 0,
          limit: params.limit || 20
        };
      } catch (fallbackError) {
        console.error('All image endpoints failed:', fallbackError);
        return { images: [], count: 0, skip: 0, limit: 20 };
      }
    }
  }

  // Get directories
  async getDirectories() {
    try {
      // Try custom post types first
      const videos = await this.get('/wp/v2/slr-videos', { per_page: 100 }).catch(() => []);
      const images = await this.get('/wp/v2/slr-images', { per_page: 100 }).catch(() => []);
      
      const directories = new Set();
      
      [...videos, ...images].forEach(item => {
        const directory = this.extractDirectory(item);
        if (directory) {
          directories.add(directory);
        }
      });

      // If no directories found, try to extract from media
      if (directories.size === 0) {
        const media = await this.get('/wp/v2/media', { per_page: 100 }).catch(() => []);
        media.forEach(item => {
          if (item.source_url) {
            const urlPath = new URL(item.source_url).pathname;
            const pathParts = urlPath.split('/');
            const yearIndex = pathParts.findIndex(part => /^\d{4}$/.test(part));
            if (yearIndex > -1 && pathParts[yearIndex + 1]) {
              directories.add(pathParts[yearIndex] + '-' + pathParts[yearIndex + 1]);
            }
          }
        });
      }

      return {
        directories: Array.from(directories).sort()
      };
    } catch (error) {
      console.error('Error getting directories:', error);
      return { directories: [] };
    }
  }

  // Helper method to get videos category ID (for fallback)
  async getVideosCategoryId() {
    try {
      const categories = await this.get('/wp/v2/categories', { search: 'videos' });
      return categories.length > 0 ? categories[0].id : null;
    } catch (error) {
      return null;
    }
  }

  // Transform regular post as video (fallback)
  transformPostAsVideo(wpPost) {
    const featuredImage = wpPost._embedded?.['wp:featuredmedia']?.[0];
    
    return {
      _id: wpPost.id.toString(),
      id: wpPost.id.toString(),
      title: decodeHtmlEntities(wpPost.title?.rendered) || 'Untitled',
      description: decodeHtmlEntities(wpPost.content?.rendered || wpPost.excerpt?.rendered) || '',
      video_url: this.extractVideoUrl(wpPost.content?.rendered),
      poster_url: featuredImage?.source_url || null,
      thumbnail_url: featuredImage?.media_details?.sizes?.medium?.source_url || featuredImage?.source_url,
      directory: this.extractDirectoryFromContent(wpPost.content?.rendered),
      filename: wpPost.slug || '',
      duration: null,
      created_at: wpPost.date,
      updated_at: wpPost.modified
    };
  }

  // Transform media as image (fallback)
  transformMediaAsImage(wpMedia) {
    return {
      _id: wpMedia.id.toString(),
      id: wpMedia.id.toString(),
      title: decodeHtmlEntities(wpMedia.title?.rendered || wpMedia.alt_text) || 'Untitled',
      description: decodeHtmlEntities(wpMedia.description?.rendered || wpMedia.caption?.rendered) || '',
      image_url: wpMedia.source_url,
      thumbnail_url: wpMedia.media_details?.sizes?.medium?.source_url || wpMedia.source_url,
      directory: this.extractDirectoryFromUrl(wpMedia.source_url),
      filename: wpMedia.media_details?.file || '',
      created_at: wpMedia.date,
      updated_at: wpMedia.modified
    };
  }

  // Extract directory from content (fallback)
  extractDirectoryFromContent(content) {
    if (!content) return null;
    const directoryMatch = content.match(/répertoire[:\s]+([^<\n]+)/i);
    return directoryMatch ? directoryMatch[1].trim() : null;
  }

  // Extract directory from URL (fallback)
  extractDirectoryFromUrl(url) {
    try {
      const urlPath = new URL(url).pathname;
      const pathParts = urlPath.split('/');
      const yearIndex = pathParts.findIndex(part => /^\d{4}$/.test(part));
      if (yearIndex > -1 && pathParts[yearIndex + 1]) {
        return pathParts[yearIndex] + '-' + pathParts[yearIndex + 1];
      }
    } catch (error) {
      // Ignore URL parsing errors
    }
    return null;
  }

  // Transform WordPress video to our format
  transformVideo(wpVideo) {
    const featuredImage = wpVideo._embedded?.['wp:featuredmedia']?.[0];
    
    return {
      _id: wpVideo.id.toString(),
      id: wpVideo.id.toString(),
      title: decodeHtmlEntities(wpVideo.title?.rendered) || 'Untitled',
      description: decodeHtmlEntities(wpVideo.content?.rendered) || '',
      video_url: this.extractVideoUrl(wpVideo.content?.rendered),
      poster_url: featuredImage?.source_url || null,
      thumbnail_url: featuredImage?.media_details?.sizes?.medium?.source_url || featuredImage?.source_url,
      directory: this.extractDirectory(wpVideo),
      filename: wpVideo.meta?.filename || '',
      duration: wpVideo.meta?.duration || null,
      created_at: wpVideo.date,
      updated_at: wpVideo.modified
    };
  }

  // Transform WordPress playlist to our format
  transformPlaylist(wpPlaylist) {
    const videoIds = wpPlaylist.meta?.video_ids ? JSON.parse(wpPlaylist.meta.video_ids) : [];
    
    // Clean and decode the description
    let cleanDescription = wpPlaylist.content?.rendered || '';
    cleanDescription = stripHtmlTags(cleanDescription);
    cleanDescription = decodeHtmlEntities(cleanDescription);
    
    // Get featured image if available
    const featuredImage = wpPlaylist._embedded?.['wp:featuredmedia']?.[0];
    
    return {
      _id: wpPlaylist.id.toString(),
      id: wpPlaylist.id.toString(),
      name: decodeHtmlEntities(wpPlaylist.title?.rendered) || 'Untitled Playlist',
      description: cleanDescription,
      video_ids: videoIds,
      video_count: wpPlaylist.meta?.video_count || videoIds.length,
      featured_image: featuredImage?.source_url || null,
      featured_image_thumbnail: featuredImage?.media_details?.sizes?.medium?.source_url || featuredImage?.source_url || null,
      created_at: wpPlaylist.date,
      updated_at: wpPlaylist.modified
    };
  }

  // Transform WordPress image to our format
  transformImage(wpImage) {
    const featuredImage = wpImage._embedded?.['wp:featuredmedia']?.[0];
    
    return {
      _id: wpImage.id.toString(),
      id: wpImage.id.toString(),
      title: decodeHtmlEntities(wpImage.title?.rendered) || 'Untitled',
      description: decodeHtmlEntities(wpImage.content?.rendered) || '',
      image_url: featuredImage?.source_url || null,
      thumbnail_url: featuredImage?.media_details?.sizes?.medium?.source_url || featuredImage?.source_url,
      directory: this.extractDirectory(wpImage),
      filename: wpImage.meta?.filename || '',
      created_at: wpImage.date,
      updated_at: wpImage.modified
    };
  }

  // Extract video URL from WordPress content
  extractVideoUrl(content) {
    if (!content) return null;
    
    // Look for video block
    const videoMatch = content.match(/"src":"([^"]+\.(?:mp4|avi|mov|wmv|flv|webm|mkv|m4v))"/i);
    if (videoMatch) {
      return videoMatch[1];
    }
    
    // Look for video tag
    const videoTagMatch = content.match(/<video[^>]+src="([^"]+)"/i);
    if (videoTagMatch) {
      return videoTagMatch[1];
    }
    
    return null;
  }

  // Extract directory from WordPress post
  extractDirectory(wpPost) {
    const filename = wpPost.meta?.filename;
    if (filename) {
      const pathParts = filename.split('/');
      return pathParts.length > 1 ? pathParts[pathParts.length - 2] : null;
    }
    return null;
  }

  // Search across all content types
  async search(query) {
    try {
      console.log('Searching for:', query);
      
      // Search videos and images simultaneously
      const [videosResult, imagesResult] = await Promise.all([
        this.getVideos({ search: query, limit: 50 }),
        this.getImages({ search: query, limit: 50 })
      ]);

      // Combine results with media_type indicator
      const allResults = [
        ...videosResult.videos.map(video => ({ ...video, media_type: 'video' })),
        ...imagesResult.images.map(image => ({ ...image, media_type: 'image' }))
      ];

      console.log(`Search found ${allResults.length} results:`, {
        videos: videosResult.videos.length,
        images: imagesResult.images.length
      });

      return {
        results: allResults,
        count: allResults.length,
        videos_count: videosResult.videos.length,
        images_count: imagesResult.images.length
      };
    } catch (error) {
      console.error('Search error:', error);
      return {
        results: [],
        count: 0,
        videos_count: 0,
        images_count: 0
      };
    }
  }
}

// Create the WordPress API client
const wpApiClient = new WordPressDirectClient();

// Updated APIClient to use WordPress directly
class APIClient {
  async get(endpoint) {
    // Map our API endpoints to WordPress API calls
    try {
      switch (endpoint) {
        case '/videos':
          return await wpApiClient.getVideos();
        
        case '/playlists':
          return await wpApiClient.getPlaylists();
        
        case '/directories':
          return await wpApiClient.getDirectories();
        
        case '/images':
          return await wpApiClient.getImages();
        
        default:
          // Handle parameterized endpoints
          if (endpoint.startsWith('/videos/')) {
            const id = endpoint.split('/')[2];
            return await wpApiClient.getVideo(id);
          }
          
          if (endpoint.startsWith('/playlists/')) {
            const id = endpoint.split('/')[2];
            return await wpApiClient.getPlaylist(id);
          }
          
          if (endpoint.includes('?')) {
            const [path, queryString] = endpoint.split('?');
            const params = new URLSearchParams(queryString);
            const paramsObj = Object.fromEntries(params);
            
            if (path === '/videos') {
              return await wpApiClient.getVideos(paramsObj);
            }
            
            if (path === '/images') {
              return await wpApiClient.getImages(paramsObj);
            }
            
            if (path === '/search') {
              const query = paramsObj.q;
              return await wpApiClient.search(query);
            }
          }
          
          // Return empty result for unsupported endpoints
          return { data: [], count: 0 };
      }
    } catch (error) {
      console.error('API Error:', error);
      // Return empty data structure on error
      return { data: [], count: 0 };
    }
  }
}

const apiClient = new APIClient();

// Content cleaning utility functions
const getCleanTitle = (video) => {
  // First try to use the title field
  if (video.title && video.title.trim()) {
    return decodeHtmlEntities(video.title.trim());
  }
  
  // Extract from WordPress content if needed
  if (video.description) {
    // Extract video caption as fallback title
    const captionMatch = video.description.match(/<figcaption[^>]*class="wp-element-caption"[^>]*>([^<]+)<\/figcaption>/i);
    if (captionMatch && captionMatch[1].trim()) {
      return decodeHtmlEntities(captionMatch[1].trim());
    }
    
    // Extract from paragraph content
    let cleanDesc = video.description.replace(/<!-- wp:[^>]+ -->/g, '');
    cleanDesc = cleanDesc.replace(/<!-- \/wp:[^>]+ -->/g, '');
    cleanDesc = cleanDesc.replace(/<figure class="wp-block-video">.*?<\/figure>/gs, '');
    
    const paragraphMatch = cleanDesc.match(/<p[^>]*>([^<]+)<\/p>/);
    if (paragraphMatch && paragraphMatch[1].trim()) {
      return decodeHtmlEntities(paragraphMatch[1].trim());
    }
  }
  
  // Fallback to filename
  return decodeHtmlEntities(video.filename) || 'Vidéo sans titre';
};

const getCleanDescription = (video) => {
  if (video.description) {
    // Remove WordPress comment blocks
    let cleanDesc = video.description.replace(/<!-- wp:[^>]+ -->/g, '');
    cleanDesc = cleanDesc.replace(/<!-- \/wp:[^>]+ -->/g, '');
    
    // Remove video blocks entirely  
    cleanDesc = cleanDesc.replace(/<figure class="wp-block-video">.*?<\/figure>/gs, '');
    
    // Extract paragraph content
    const paragraphMatch = cleanDesc.match(/<p[^>]*>([^<]+)<\/p>/);
    if (paragraphMatch && paragraphMatch[1].trim()) {
      return decodeHtmlEntities(paragraphMatch[1].trim());
    }
    
    // If no paragraph, try to clean remaining HTML
    cleanDesc = cleanDesc.replace(/<[^>]+>/g, '').trim();
    return cleanDesc ? decodeHtmlEntities(cleanDesc) : null;
  }
  return null;
};

const getVideoCaption = (video) => {
  if (video.description) {
    // Look for figcaption in WordPress content
    const captionMatch = video.description.match(/<figcaption[^>]*class="wp-element-caption"[^>]*>([^<]+)<\/figcaption>/i);
    if (captionMatch) {
      return decodeHtmlEntities(captionMatch[1].trim());
    }
  }
  return null;
};

const getVideoPoster = (video) => {
  // Try featured image first
  if (video.thumbnail_path) {
    return video.thumbnail_path;
  }
  
  // Try to extract poster from video tag
  if (video.description) {
    const posterMatch = video.description.match(/poster="([^"]+)"/i);
    if (posterMatch) {
      return posterMatch[1];
    }
  }
  
  // Default fallback
  return 'https://images.unsplash.com/photo-1562552052-4e9f2d8e8a4e?w=320&h=240&fit=crop';
};

// Header Component
export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="bg-[#36a4bc] shadow-lg">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-2xl font-bold text-[#36a4bc]">
                Saint-Louis Ruche
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex space-x-6">
                <Link to="/" className="text-gray-700 hover:text-[#36a4bc] font-medium">
                  Accueil
                </Link>
                <Link to="/federaux" className="text-gray-700 hover:text-[#36a4bc] font-medium">
                  Fédéraux
                </Link>
                <Link to="/fete-de-gym" className="text-gray-700 hover:text-[#36a4bc] font-medium">
                  Fête de Gym
                </Link>
                <Link to="/divers" className="text-gray-700 hover:text-[#36a4bc] font-medium">
                  Divers
                </Link>
              </div>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center max-w-md mx-4 flex-1">
              <div className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher des vidéos..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-l-full focus:outline-none focus:ring-2 focus:ring-[#36a4bc]"
                />
                <button
                  type="submit"
                  className="absolute right-0 top-0 h-full px-4 bg-[#36a4bc] text-white rounded-r-full hover:bg-[#2a8298] transition-colors"
                >
                  🔍
                </button>
              </div>
            </form>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-[#36a4bc]"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher des vidéos..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-l-full focus:outline-none focus:ring-2 focus:ring-[#36a4bc]"
                  />
                  <button
                    type="submit"
                    className="absolute right-0 top-0 h-full px-4 bg-[#36a4bc] text-white rounded-r-full hover:bg-[#2a8298] transition-colors"
                  >
                    🔍
                  </button>
                </div>
              </form>
              <div className="space-y-2">
                <Link to="/" className="block py-2 px-4 text-gray-700 hover:bg-gray-100 rounded">
                  Accueil
                </Link>
                <Link to="/federaux" className="block py-2 px-4 text-gray-700 hover:bg-gray-100 rounded">
                  Fédéraux
                </Link>
                <Link to="/fete-de-gym" className="block py-2 px-4 text-gray-700 hover:bg-gray-100 rounded">
                  Fête de Gym
                </Link>
                <Link to="/divers" className="block py-2 px-4 text-gray-700 hover:bg-gray-100 rounded">
                  Divers
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

// Video Card Component
export const VideoCard = ({ video }) => {
  const posterUrl = getVideoPoster(video);
  const cleanTitle = getCleanTitle(video);
  const cleanDescription = getCleanDescription(video);

  const formatDuration = (duration) => {
    if (!duration) return '';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return '';
    }
  };

  return (
    <Link to={`/video/${video._id}`} className="group">
      <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
        <div className="relative">
          <img
            src={posterUrl}
            alt={cleanTitle}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1562552052-4e9f2d8e8a4e?w=320&h=240&fit=crop';
            }}
          />
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded">
            {formatDuration(video.metadata?.duration)}
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-[#36a4bc] transition-colors">
            {cleanTitle}
          </h3>
          {cleanDescription && (
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">
              {cleanDescription}
            </p>
          )}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{formatDate(video.date)}</span>
            <span>{video.metadata?.width && video.metadata?.height ? `${video.metadata.width}x${video.metadata.height}` : ''}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Image Card Component
export const ImageCard = ({ image, onClick }) => {
  const thumbnailUrl = image.thumbnail_path 
    ? `${process.env.REACT_APP_BACKEND_URL}/static/${image.thumbnail_path.split('/').pop()}`
    : `${process.env.REACT_APP_BACKEND_URL}/api/media/image/${image.filename}`;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR');
    } catch {
      return '';
    }
  };

  return (
    <div className="group cursor-pointer" onClick={() => onClick(image)}>
      <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
        <div className="relative">
          <img
            src={thumbnailUrl}
            alt={image.description || image.filename}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1503198129995-3f110c24aaa0?w=320&h=240&fit=crop';
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
            <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="text-2xl">🔍</div>
            </div>
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2">{image.description || image.filename}</h3>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{formatDate(image.date)}</span>
            <span>{image.metadata?.width && image.metadata?.height ? `${image.metadata.width}x${image.metadata.height}` : ''}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Home Page Component
export const HomePage = () => {
  const [videos, setVideos] = useState([]);
  const [directories, setDirectories] = useState([]);
  const [selectedDirectory, setSelectedDirectory] = useState('Tous');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAccueilPlaylistVideos(), // Only load "Accueil" playlist videos
        fetchDirectories()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch videos from "Accueil" playlist only
  const fetchAccueilPlaylistVideos = async () => {
    try {
      // First get all playlists to find "Accueil"
      const playlistResponse = await apiClient.get('/playlists');
      const playlists = playlistResponse.playlists || [];
      
      // Find the "Accueil" playlist
      const accueilPlaylist = playlists.find(p => 
        p.name && p.name.toLowerCase() === 'accueil'
      );
      
      if (accueilPlaylist) {
        // Get the "Accueil" playlist with its videos
        const playlistDetails = await apiClient.get(`/playlists/${accueilPlaylist.id}`);
        const videos = playlistDetails.videos || [];
        setVideos(videos);
        console.log(`Loaded ${videos.length} videos from "Accueil" playlist`);
      } else {
        console.warn('No "Accueil" playlist found, showing no videos');
        setVideos([]);
      }
    } catch (error) {
      console.error('Error fetching Accueil playlist videos:', error);
      setVideos([]);
    }
  };

  const fetchDirectories = async () => {
    try {
      const response = await apiClient.get('/directories');
      setDirectories(['Tous', ...(response.directories || [])]);
    } catch (error) {
      console.error('Error fetching directories:', error);
      setDirectories(['Tous']);
    }
  };


  const handleDirectoryChange = (directory) => {
    // Directory filtering is disabled when showing only "Accueil" playlist
    // This function is kept for compatibility but does nothing
    console.log('Directory filtering disabled - showing only Accueil playlist videos');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des vidéos...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section - At the very top */}
      <div className="hero-image-container relative w-full h-64 sm:h-80 md:h-96 lg:h-[28rem] xl:h-[32rem]">
        <img
          src="/medias/hero-image.jpg"
          alt="Saint-Louis Ruche"
          className="w-full h-full object-cover object-center"
          onError={(e) => {
            // Fallback to a simple placeholder if local image not found
            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='400' viewBox='0 0 800 400'%3E%3Crect width='800' height='400' fill='%2336a4bc'/%3E%3Ctext x='400' y='200' text-anchor='middle' dy='0.3em' font-family='Arial, sans-serif' font-size='32' fill='white'%3ESaint-Louis Ruche%3C/text%3E%3C/svg%3E";
          }}
        />
      </div>

      {/* Header (Navigation + Search) - Below hero image */}
      <Header />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Directory Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {directories.map(directory => (
              <button
                key={directory}
                onClick={() => handleDirectoryChange(directory)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedDirectory === directory
                    ? 'bg-[#36a4bc] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {directory}
              </button>
            ))}
          </div>
        </div>

        {/* Video Grid */}
        {videos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {videos.map(video => (
              <VideoCard key={video._id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 mb-12">
            <div className="text-6xl mb-4">📹</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucune vidéo trouvée</h2>
            <p className="text-gray-600">
              Aucune vidéo dans la playlist "Accueil" ou la playlist n'existe pas.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

// Video Player Component
export const VideoPlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadVideo();
    }
  }, [id]);

  const loadVideo = async () => {
    setLoading(true);
    try {
      const [videoResponse, playlistsResponse] = await Promise.all([
        apiClient.get(`/videos/${id}`),
        apiClient.get('/playlists') // Get playlists instead of all videos
      ]);
      
      setVideo(videoResponse);
      
      console.log('Current video ID:', id);
      console.log('Available playlists:', playlistsResponse.playlists?.length || 0);
      
      // Find playlists that contain the current video
      const currentVideoPlaylists = [];
      
      for (const playlist of playlistsResponse.playlists || []) {
        try {
          // Get full playlist details with videos
          const playlistDetails = await apiClient.get(`/playlists/${playlist.id}`);
          
          // Check if current video is in this playlist
          const isVideoInPlaylist = playlistDetails.videos?.some(v => v._id === id || v.id === id);
          
          if (isVideoInPlaylist) {
            currentVideoPlaylists.push({
              ...playlistDetails,
              videos: playlistDetails.videos || []
            });
            console.log(`Video found in playlist: "${playlistDetails.name}" (${playlistDetails.videos?.length || 0} videos)`);
          }
        } catch (error) {
          console.error(`Error checking playlist ${playlist.id}:`, error);
        }
      }
      
      console.log(`Video is in ${currentVideoPlaylists.length} playlist(s)`);
      
      // Collect all videos from the same playlists
      let similarVideos = [];
      
      currentVideoPlaylists.forEach(playlist => {
        const playlistVideos = playlist.videos || [];
        // Add videos from this playlist, excluding the current video
        const otherVideos = playlistVideos.filter(v => v._id !== id && v.id !== id);
        similarVideos = similarVideos.concat(otherVideos);
        
        console.log(`Added ${otherVideos.length} videos from playlist "${playlist.name}"`);
      });
      
      // Remove duplicates (in case video is in multiple playlists)
      const uniqueSimilarVideos = [];
      const seenIds = new Set();
      
      similarVideos.forEach(video => {
        const videoId = video._id || video.id;
        if (!seenIds.has(videoId)) {
          seenIds.add(videoId);
          uniqueSimilarVideos.push(video);
        }
      });
      
      console.log(`Found ${uniqueSimilarVideos.length} unique similar videos from playlists`);
      
      // Show up to 4 similar videos
      setRelatedVideos(uniqueSimilarVideos.slice(0, 4));
      
    } catch (error) {
      console.error('Error loading video:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Vidéo non trouvée</h2>
        <Link to="/" className="text-[#36a4bc] hover:underline">Retour à l'accueil</Link>
      </div>
    );
  }

  // Extract video URL from WordPress content only
  const getVideoUrl = (video) => {
    if (video.description) {
      // Look for video src in WordPress content
      const videoMatch = video.description.match(/src="([^"]*\.(mp4|webm|ogg|avi|mov|mkv)[^"]*)"/i);
      if (videoMatch) {
        return videoMatch[1];
      }
    }
    
    // No embedded video found
    return null;
  };

  // Extract video caption from WordPress content
  const getVideoCaption = (video) => {
    if (video.description) {
      // Look for figcaption in WordPress content
      const captionMatch = video.description.match(/<figcaption[^>]*class="wp-element-caption"[^>]*>([^<]+)<\/figcaption>/i);
      if (captionMatch) {
        return captionMatch[1].trim();
      }
    }
    return null;
  };

  // Extract clean description (remove WordPress blocks)
  const getCleanDescription = (video) => {
    if (video.description) {
      // Remove WordPress comment blocks
      let cleanDesc = video.description.replace(/<!-- wp:[^>]+ -->/g, '');
      cleanDesc = cleanDesc.replace(/<!-- \/wp:[^>]+ -->/g, '');
      
      // Remove video blocks entirely  
      cleanDesc = cleanDesc.replace(/<figure class="wp-block-video">.*?<\/figure>/gs, '');
      
      // Extract paragraph content
      const paragraphMatch = cleanDesc.match(/<p[^>]*>([^<]+)<\/p>/);
      if (paragraphMatch && paragraphMatch[1].trim()) {
        return paragraphMatch[1].trim();
      }
      
      // If no paragraph, try to clean remaining HTML
      cleanDesc = cleanDesc.replace(/<[^>]+>/g, '').trim();
      return cleanDesc || null;
    }
    return null;
  };

  const videoUrl = getVideoUrl(video);
  const videoCaption = getVideoCaption(video);
  const cleanDescription = getCleanDescription(video);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-[#36a4bc] hover:text-[#2a8298] transition-colors"
        >
          <span className="text-xl">←</span>
          <span className="font-medium">Retour</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Video */}
        <div className="lg:col-span-2">
          <div className="bg-black rounded-lg overflow-hidden mb-4">
            <div className="aspect-video">
              {videoUrl ? (
                <video 
                  controls 
                  className="w-full h-full"
                  poster={getVideoPoster(video)}
                >
                  <source src={videoUrl} type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                  <div className="text-center">
                    <div className="text-4xl mb-4">🎬</div>
                    <h3 className="text-xl font-semibold mb-2">Vidéo non disponible</h3>
                    <p className="text-gray-300 mb-4">Cette vidéo ne contient pas de fichier vidéo intégré.</p>
                    <p className="text-sm text-gray-400 mb-4">
                      Pour afficher une vidéo, ajoutez un bloc vidéo dans le contenu WordPress.
                    </p>
                    {video.url && (
                      <a 
                        href={video.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block mt-4 bg-[#36a4bc] text-white px-4 py-2 rounded hover:bg-[#2a8298] transition-colors"
                      >
                        Voir sur WordPress
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {getCleanTitle(video)}
            </h1>
            
            {/* Video Caption/Legend */}
            {videoCaption && (
              <div className="bg-[#36a4bc] bg-opacity-10 border-l-4 border-[#36a4bc] p-4 mb-4">
                <p className="text-[#36a4bc] font-medium">{videoCaption}</p>
              </div>
            )}
            
            {/* Description */}
            {cleanDescription && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700">{cleanDescription}</p>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                {video.date && (
                  <span>{new Date(video.date).toLocaleDateString('fr-FR')}</span>
                )}
                {video.metadata?.width && video.metadata?.height && (
                  <>
                    {video.date && <span>•</span>}
                    <span>{video.metadata.width}x{video.metadata.height}</span>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button className="flex items-center space-x-1 px-3 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                  <span>👍</span>
                  <span className="text-sm">J'aime</span>
                </button>
                <button 
                  onClick={() => navigator.share && navigator.share({
                    title: video.title || videoCaption || 'Vidéo Saint-Louis Ruche',
                    url: window.location.href
                  })}
                  className="flex items-center space-x-1 px-3 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <span>📤</span>
                  <span className="text-sm">Partager</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Vidéos Similaires</h2>
            <div className="space-y-4">
              {relatedVideos.map(relatedVideo => {
                const relatedPoster = getVideoPoster(relatedVideo);
                const relatedTitle = getCleanTitle(relatedVideo);
                
                return (
                  <Link
                    key={relatedVideo._id}
                    to={`/video/${relatedVideo._id}`}
                    className="flex space-x-3 group"
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src={relatedPoster}
                        alt={relatedTitle}
                        className="w-24 h-16 object-cover rounded group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1562552052-4e9f2d8e8a4e?w=96&h=64&fit=crop';
                        }}
                      />
                      <div className="absolute bottom-1 right-1 bg-black bg-opacity-80 text-white text-xs px-1 rounded">
                        {relatedVideo.metadata?.duration ? `${Math.floor(relatedVideo.metadata.duration / 60)}:${Math.floor(relatedVideo.metadata.duration % 60).toString().padStart(2, '0')}` : 'N/A'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 group-hover:text-[#36a4bc] transition-colors">
                        {relatedTitle}
                      </h3>
                      {relatedVideo.date && (
                        <p className="text-xs text-gray-600 mt-1">
                          {new Date(relatedVideo.date).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Playlist Page Component
export const PlaylistPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadPlaylist();
    }
  }, [id]);

  const loadPlaylist = async () => {
    try {
      const response = await apiClient.get(`/playlists/${id}`);
      setPlaylist(response);
    } catch (error) {
      console.error('Error loading playlist:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Playlist non trouvée</h2>
        <Link to="/" className="text-[#36a4bc] hover:underline">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-[#36a4bc] hover:text-[#2a8298] font-medium mb-6 transition-colors"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Retour
      </button>

      <div className="bg-white rounded-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-4 md:space-y-0">
          {/* Featured Image or Dark Blue Fallback */}
          <div className="relative w-full md:w-48 h-48 flex-shrink-0 rounded-lg overflow-hidden">
            {playlist.featured_image_thumbnail || playlist.featured_image ? (
              <img
                src={playlist.featured_image_thumbnail || playlist.featured_image}
                alt={decodeHtmlEntities(playlist.name)}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to dark blue background if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            
            {/* Dark Blue Fallback */}
            <div 
              className="w-full h-full bg-gradient-to-br from-blue-800 to-blue-900 flex items-center justify-center"
              style={{ display: playlist.featured_image_thumbnail || playlist.featured_image ? 'none' : 'flex' }}
            >
              <div className="text-center text-white">
                <div className="text-6xl font-bold mb-2">
                  {playlist.name.charAt(0).toUpperCase()}
                </div>
                <div className="text-sm opacity-80">
                  {playlist.videos?.length || 0} vidéo{(playlist.videos?.length || 0) !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{decodeHtmlEntities(playlist.name)}</h1>
            {playlist.description && (
              <p className="text-gray-600 mb-4">{decodeHtmlEntities(stripHtmlTags(playlist.description))}</p>
            )}
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>{playlist.videos?.length || 0} vidéo{(playlist.videos?.length || 0) !== 1 ? 's' : ''}</span>
              <span>•</span>
              <span>Saint-Louis Ruche</span>
            </div>
          </div>
        </div>
      </div>

      {playlist.videos?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {playlist.videos.map(video => (
            <VideoCard key={video._id} video={video} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📹</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Playlist vide</h2>
          <p className="text-gray-600">Cette playlist ne contient pas encore de vidéos.</p>
        </div>
      )}
    </div>
  );
};

// Gallery Page Component
export const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await apiClient.get('/images');
      setImages(response.images || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Galerie Photos</h1>
        <p className="text-gray-600">Découvrez les moments forts de Saint-Louis Ruche en images</p>
      </div>

      {images.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {images.map(image => (
            <ImageCard key={image._id} image={image} onClick={setSelectedImage} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🖼️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucune image trouvée</h2>
          <p className="text-gray-600">Aucune image n'a encore été synchronisée depuis votre serveur FTP.</p>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300 z-10"
            >
              ✕
            </button>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/media/image/${selectedImage.filename}`}
              alt={selectedImage.description || selectedImage.filename}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onError={(e) => {
                e.target.src = selectedImage.thumbnail_path 
                  ? `${process.env.REACT_APP_BACKEND_URL}/static/${selectedImage.thumbnail_path.split('/').pop()}`
                  : 'https://images.unsplash.com/photo-1503198129995-3f110c24aaa0?w=800&h=600&fit=crop';
              }}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-80 text-white p-4 rounded-b-lg">
              <h3 className="text-lg font-semibold mb-1">{selectedImage.description || selectedImage.filename}</h3>
              <div className="text-sm text-gray-300">
                {selectedImage.date && (
                  <span>{new Date(selectedImage.date).toLocaleDateString('fr-FR')}</span>
                )}
                {selectedImage.metadata?.width && selectedImage.metadata?.height && (
                  <span>
                    {selectedImage.date && ' • '}
                    {selectedImage.metadata.width}x{selectedImage.metadata.height}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Search Results Component
export const SearchResults = () => {
  const location = useLocation();
  const searchQuery = new URLSearchParams(location.search).get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (searchQuery) {
      searchMedia();
    }
  }, [searchQuery]);

  const searchMedia = async () => {
    try {
      const response = await apiClient.get(`/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.results || []);
    } catch (error) {
      console.error('Error searching media:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Résultats pour "{searchQuery}"
        </h1>
        <p className="text-gray-600">{results.length} résultat(s) trouvé(s)</p>
      </div>

      {results.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {results.map(item => (
            item.media_type === 'video' ? (
              <VideoCard key={item._id} video={item} />
            ) : (
              <ImageCard key={item._id} image={item} onClick={() => {}} />
            )
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucun résultat trouvé</h2>
          <p className="text-gray-600">Essayez avec d'autres mots-clés</p>
        </div>
      )}
    </div>
  );
};

// Helper function to extract category from playlist name brackets
const extractPlaylistCategory = (playlistName) => {
  if (!playlistName) return 'divers';
  
  console.log('Analyzing playlist:', playlistName);
  
  // Look for text in brackets like [Fédéraux 2024] or [Fête Gym]
  // Try different bracket patterns including square brackets, parentheses, and Unicode variations
  const bracketPatterns = [
    /\[([^\]]+)\]/,  // Standard square brackets [content]
    /\(([^\)]+)\)/,  // Parentheses (content)
    /\【([^\】]+)\】/, // Unicode square brackets
    /\〔([^\〕]+)\〕/  // Unicode parentheses
  ];
  
  let bracketContent = null;
  
  for (const pattern of bracketPatterns) {
    const match = playlistName.match(pattern);
    if (match) {
      bracketContent = match[1].toLowerCase().trim();
      console.log('Found bracket content:', bracketContent);
      break;
    }
  }
  
  if (bracketContent) {
    // Check for Fédéraux with various spellings
    if (bracketContent.includes('fédéraux') || 
        bracketContent.includes('federaux') || 
        bracketContent.includes('fédéral') ||
        bracketContent.includes('federal')) {
      console.log('Categorized as federaux');
      return 'federaux';
    }
    
    // Check for Fête with various spellings
    if (bracketContent.includes('fête') || 
        bracketContent.includes('fete') ||
        bracketContent.includes('gym') ||
        bracketContent.includes('spectacle') ||
        bracketContent.includes('gala')) {
      console.log('Categorized as fete');
      return 'fete';
    }
  }
  
  // Also check without brackets - sometimes the category might be in the main title
  const lowerName = playlistName.toLowerCase();
  if (lowerName.includes('fédéraux') || lowerName.includes('federaux') || lowerName.includes('fédéral')) {
    console.log('Categorized as federaux (from main title)');
    return 'federaux';
  }
  
  if (lowerName.includes('fête') || lowerName.includes('fete') || 
      (lowerName.includes('gym') && (lowerName.includes('spectacle') || lowerName.includes('gala')))) {
    console.log('Categorized as fete (from main title)');
    return 'fete';
  }
  
  console.log('Categorized as divers');
  return 'divers'; // Default category for playlists without specific categories
};

// Fédéraux Playlists Page Component
export const FederauxPage = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const response = await apiClient.get('/playlists');
      const allPlaylists = response.playlists || [];
      
      console.log('All playlists received:', allPlaylists.length);
      allPlaylists.forEach(playlist => {
        console.log('Playlist:', playlist.name, '-> Category:', extractPlaylistCategory(playlist.name));
      });
      
      // Filter playlists with 'Fédéraux' in brackets and sort by reverse alphabetical order
      const filteredPlaylists = allPlaylists
        .filter(playlist => {
          if (!playlist.name || playlist.name.toLowerCase() === 'accueil') return false;
          return extractPlaylistCategory(playlist.name) === 'federaux';
        })
        .sort((a, b) => b.name.localeCompare(a.name, 'fr', { sensitivity: 'base' }));
      
      console.log('Fédéraux playlists found:', filteredPlaylists.length);
      setPlaylists(filteredPlaylists);
    } catch (error) {
      console.error('Error fetching Fédéraux playlists:', error);
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des playlists Fédéraux...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Fédéraux</h1>
        <p className="text-gray-600">{playlists.length} playlist(s) Fédéraux disponible(s)</p>
      </div>

      {playlists.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {playlists.map(playlist => (
            <Link
              key={playlist._id}
              to={`/playlist/${playlist._id}`}
              className="group block"
            >
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200">
                {/* Featured Image or Dark Blue Fallback */}
                <div className="relative h-48 overflow-hidden">
                  {playlist.featured_image_thumbnail || playlist.featured_image ? (
                    <img
                      src={playlist.featured_image_thumbnail || playlist.featured_image}
                      alt={decodeHtmlEntities(playlist.name)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  <div 
                    className="w-full h-full bg-gradient-to-br from-blue-800 to-blue-900 flex items-center justify-center"
                    style={{ display: playlist.featured_image_thumbnail || playlist.featured_image ? 'none' : 'flex' }}
                  >
                    <div className="text-center text-white">
                      <div className="text-4xl font-bold mb-2">
                        {playlist.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-sm opacity-80">
                        {playlist.video_count || 0} vidéo{(playlist.video_count || 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300"></div>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#36a4bc] transition-colors mb-2">
                      {decodeHtmlEntities(playlist.name)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {playlist.video_count || 0} vidéo(s)
                    </p>
                  </div>
                  
                  {playlist.description && (
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {decodeHtmlEntities(stripHtmlTags(playlist.description))}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[#36a4bc] font-medium text-sm group-hover:text-[#2a8298] transition-colors">
                      Voir la playlist →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏆</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucune playlist Fédéraux trouvée</h2>
          <p className="text-gray-600">Les playlists avec [Fédéraux] apparaîtront ici</p>
        </div>
      )}
    </div>
  );
};

// Fête de Gym Playlists Page Component
export const FeteDeGymPage = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const response = await apiClient.get('/playlists');
      const allPlaylists = response.playlists || [];
      
      console.log('All playlists for Fête de Gym:', allPlaylists.length);
      
      // Filter playlists with 'Fête' in brackets and sort by reverse alphabetical order
      const filteredPlaylists = allPlaylists
        .filter(playlist => {
          if (!playlist.name || playlist.name.toLowerCase() === 'accueil') return false;
          const category = extractPlaylistCategory(playlist.name);
          console.log('Fête filter - Playlist:', playlist.name, '-> Category:', category);
          return category === 'fete';
        })
        .sort((a, b) => b.name.localeCompare(a.name, 'fr', { sensitivity: 'base' }));
      
      console.log('Fête de Gym playlists found:', filteredPlaylists.length);
      setPlaylists(filteredPlaylists);
    } catch (error) {
      console.error('Error fetching Fête de Gym playlists:', error);
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des playlists Fête de Gym...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Fête de Gym</h1>
        <p className="text-gray-600">{playlists.length} playlist(s) Fête de Gym disponible(s)</p>
      </div>

      {playlists.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {playlists.map(playlist => (
            <Link
              key={playlist._id}
              to={`/playlist/${playlist._id}`}
              className="group block"
            >
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200">
                {/* Featured Image or Dark Blue Fallback */}
                <div className="relative h-48 overflow-hidden">
                  {playlist.featured_image_thumbnail || playlist.featured_image ? (
                    <img
                      src={playlist.featured_image_thumbnail || playlist.featured_image}
                      alt={decodeHtmlEntities(playlist.name)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  <div 
                    className="w-full h-full bg-gradient-to-br from-blue-800 to-blue-900 flex items-center justify-center"
                    style={{ display: playlist.featured_image_thumbnail || playlist.featured_image ? 'none' : 'flex' }}
                  >
                    <div className="text-center text-white">
                      <div className="text-4xl font-bold mb-2">
                        {playlist.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-sm opacity-80">
                        {playlist.video_count || 0} vidéo{(playlist.video_count || 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300"></div>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#36a4bc] transition-colors mb-2">
                      {decodeHtmlEntities(playlist.name)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {playlist.video_count || 0} vidéo(s)
                    </p>
                  </div>
                  
                  {playlist.description && (
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {decodeHtmlEntities(stripHtmlTags(playlist.description))}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[#36a4bc] font-medium text-sm group-hover:text-[#2a8298] transition-colors">
                      Voir la playlist →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎪</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucune playlist Fête de Gym trouvée</h2>
          <p className="text-gray-600">Les playlists avec [Fête] apparaîtront ici</p>
        </div>
      )}
    </div>
  );
};

// Divers Playlists Page Component (renamed from PlaylistsListPage)
export const DiversPage = () => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const response = await apiClient.get('/playlists');
      const allPlaylists = response.playlists || [];
      
      console.log('All playlists for Divers:', allPlaylists.length);
      
      // Filter out "Accueil" and specific category playlists, sort by reverse alphabetical order
      const filteredPlaylists = allPlaylists
        .filter(playlist => {
          if (!playlist.name || playlist.name.toLowerCase() === 'accueil') return false;
          const category = extractPlaylistCategory(playlist.name);
          console.log('Divers filter - Playlist:', playlist.name, '-> Category:', category);
          return category === 'divers'; // Only show playlists that don't match specific categories
        })
        .sort((a, b) => b.name.localeCompare(a.name, 'fr', { sensitivity: 'base' }));
      
      console.log('Divers playlists found:', filteredPlaylists.length);
      setPlaylists(filteredPlaylists);
    } catch (error) {
      console.error('Error fetching Divers playlists:', error);
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des playlists Divers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Divers</h1>
        <p className="text-gray-600">{playlists.length} playlist(s) Divers disponible(s)</p>
      </div>

      {playlists.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {playlists.map(playlist => (
            <Link
              key={playlist._id}
              to={`/playlist/${playlist._id}`}
              className="group block"
            >
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200">{/* Featured Image or Dark Blue Fallback */}
                <div className="relative h-48 overflow-hidden">
                  {playlist.featured_image_thumbnail || playlist.featured_image ? (
                    <img
                      src={playlist.featured_image_thumbnail || playlist.featured_image}
                      alt={decodeHtmlEntities(playlist.name)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  <div 
                    className="w-full h-full bg-gradient-to-br from-blue-800 to-blue-900 flex items-center justify-center"
                    style={{ display: playlist.featured_image_thumbnail || playlist.featured_image ? 'none' : 'flex' }}
                  >
                    <div className="text-center text-white">
                      <div className="text-4xl font-bold mb-2">
                        {playlist.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-sm opacity-80">
                        {playlist.video_count || 0} vidéo{(playlist.video_count || 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300"></div>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#36a4bc] transition-colors mb-2">
                      {decodeHtmlEntities(playlist.name)}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {playlist.video_count || 0} vidéo(s)
                    </p>
                  </div>
                  
                  {playlist.description && (
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {decodeHtmlEntities(stripHtmlTags(playlist.description))}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-[#36a4bc] font-medium text-sm group-hover:text-[#2a8298] transition-colors">
                      Voir la playlist →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucune playlist Divers trouvée</h2>
          <p className="text-gray-600">Les autres playlists apparaîtront ici</p>
        </div>
      )}
    </div>
  );
};